(function () {
  // Gift-with-purchase auto-add / auto-remove for the cart drawer.
  // Config is rendered server-side by snippets/dlux-cart-free-ship.liquid onto
  // [data-dlux-gwp], sourced from the shop metafield dlux.gift_with_purchase.
  // The progress bar copy and the locked gift line markup are rendered in Liquid
  // (recomputed on every section re-render); this script only mutates the cart
  // so the gift line appears or disappears as the qualifying subtotal crosses
  // the threshold. The qualifying subtotal excludes the gift line itself, so the
  // add -> re-render -> recheck cycle settles instead of looping.
  //
  // Every POST to /cart/add, /cart/change or /cart/update that asks for sections
  // is routed through here, so the drawer paints once with the gift already
  // right. Fastest path first:
  //   1. Add, crossing predicted (addWithGift): the product and the gift go in
  //      ONE request. Needs the added variant's price up front (product page
  //      analytics meta, or window.dluxGwpPriceHints).
  //   2. Quantity change, crossing predicted (changeThenSync): the change is
  //      sent WITHOUT sections (skips a drawer render, ~400 ms), then the gift
  //      add/remove carries the sections. The prediction reads the line's price
  //      from data-dlux-line-cents on its quantity input.
  //   3. Anything else (writeThenSync): the write goes as-is; if its sections
  //      show the threshold was crossed anyway, the gift step follows before
  //      the caller sees the response.
  // Predictions only choose the path. Every result is checked against what the
  // server returned, and a wrong guess is corrected before anything paints.
  //
  // reconcile() is the safety net for writes that skip all of that (no
  // sections requested), discount codes, another tab, and a changed GWP config.
  // It stays quiet after writes this script already verified (CHECKED_FLAG) and
  // trusts the server-rendered config on page load instead of fetching cart.js.

  var GIFT_PROPERTY = '_gift';
  // Response's sections include a gift add/remove, but its items/total describe
  // the cart BEFORE that step (callers need the original line numbering).
  // reconcile() must not seed from it or it would redo the gift step.
  var SYNCED_FLAG = '_dlux_gift_synced';
  // Gift state was verified against this response's own sections; the
  // cartUpdate that follows needs no reconcile at all.
  var CHECKED_FLAG = '_dlux_gift_checked';

  var busy = false;
  // A cartUpdate arrived while an add/remove was in flight; re-check once the
  // in-flight work settles instead of silently dropping the event.
  var rerunAfterBusy = false;
  var debounceTimer = null;
  // Cart payload handed to us by the most recent cartUpdate event, reused by
  // reconcile() so a quantity/variant change doesn't trigger an extra cart.js.
  var pendingCart = null;
  // True while every trigger queued for the next reconcile came with a freshly
  // server-rendered drawer (page load, theme editor section load).
  var trustDomOnly = true;

  // Captured before this script wraps window.fetch, so the gift's own requests
  // never re-enter the interceptor.
  var nativeFetch = window.fetch.bind(window);

  // Other scripts can register prices they already know, e.g.
  // window.dluxGwpPriceHints[variantId] = 2800, so their adds can take the
  // single-request path too.
  var priceHints = window.dluxGwpPriceHints = window.dluxGwpPriceHints || {};

  // ── Config and cart helpers ───────────────────────────────────────────────

  function cartRoot() {
    return window.Shopify && window.Shopify.routes && window.Shopify.routes.root ? window.Shopify.routes.root : '/';
  }

  function readConfigFrom(el) {
    if (!el) return null;
    var num = function (name) {
      var n = parseInt(el.getAttribute(name), 10);
      return Number.isFinite(n) ? n : 0;
    };
    return {
      enabled: el.getAttribute('data-gwp-enabled') === 'true',
      thresholdCents: num('data-gwp-threshold-cents'),
      qualifyingCents: num('data-gwp-qualifying-cents'),
      variantId: (el.getAttribute('data-gwp-variant-id') || '').trim(),
      giftAvailable: el.getAttribute('data-gwp-gift-available') === 'true',
      hasGift: el.getAttribute('data-gwp-has-gift') === 'true',
      giftQty: num('data-gwp-gift-qty')
    };
  }

  function readConfig() {
    return readConfigFrom(document.querySelector('[data-dlux-gwp]'));
  }

  function giftIsOffered(config) {
    return !!(config && config.enabled && config.giftAvailable && config.variantId && config.thresholdCents > 0);
  }

  // Gift state already right for this config: earned <=> present, never doubled.
  function isSettled(config) {
    var earned = giftIsOffered(config) && config.qualifyingCents >= config.thresholdCents;
    return earned === config.hasGift && config.giftQty <= 1;
  }

  function getCart() {
    return nativeFetch(cartRoot() + 'cart.js', { headers: { Accept: 'application/json' } }).then(function (r) {
      if (!r.ok) throw new Error('cart');
      return r.json();
    });
  }

  function isGiftLine(line) {
    return !!(line && line.properties && String(line.properties[GIFT_PROPERTY]) === 'true');
  }

  // Post-discount, non-gift subtotal in cents (Option B). Starts from
  // cart.total_price so the gate matches the Subtotal the customer sees, then
  // subtracts any gift line (shown at full price until checkout) so the gift's
  // own 100% discount can never affect the gate. Mirrors the Liquid gate in
  // dlux-cart-free-ship.liquid. NB: order-discount codes reduce this, by design.
  function qualifyingSubtotal(cart) {
    var sum = (cart && Number(cart.total_price)) || 0;
    var lines = (cart && cart.items) || [];
    for (var i = 0; i < lines.length; i++) {
      if (isGiftLine(lines[i])) sum -= Number(lines[i].final_line_price) || 0;
    }
    return sum < 0 ? 0 : sum;
  }

  function findGiftLine(cart) {
    var lines = (cart && cart.items) || [];
    for (var i = 0; i < lines.length; i++) {
      if (isGiftLine(lines[i])) return lines[i];
    }
    return null;
  }

  function sectionsToRender() {
    var cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer && typeof cartDrawer.getSectionsToRender === 'function') {
      return cartDrawer.getSectionsToRender().map(function (s) { return s.id; });
    }
    var pageCart = document.querySelector('cart-items');
    if (pageCart && typeof pageCart.getSectionsToRender === 'function') {
      return pageCart.getSectionsToRender().map(function (s) { return s.section; });
    }
    return [];
  }

  function rerender(response) {
    var cartDrawer = document.querySelector('cart-drawer');
    var publishUpdate = (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined')
      ? publish(PUB_SUB_EVENTS.cartUpdate, { source: 'dlux-cart-gift', cartData: response })
      : Promise.resolve();
    return publishUpdate.then(function () {
      if (cartDrawer && typeof cartDrawer.renderContents === 'function') {
        // Keep the drawer open if the customer already has it open, but don't
        // pop it open for a background correction (e.g. the page-load reconcile
        // after the merchant changed the GWP config or the gift went OOS).
        cartDrawer.renderContents(response, { open: cartDrawer.classList.contains('active') });
      }
    });
  }

  function postJson(path, body) {
    return nativeFetch(cartRoot() + path, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    }).then(function (r) { return r.json(); });
  }

  function giftItem(variantId) {
    var item = { id: Number(variantId), quantity: 1, properties: {} };
    item.properties[GIFT_PROPERTY] = 'true';
    return item;
  }

  function addGift(variantId, sections, sectionsUrl) {
    return postJson('cart/add.js', {
      items: [giftItem(variantId)],
      sections: sections || sectionsToRender(),
      sections_url: sectionsUrl || window.location.pathname
    });
  }

  // quantity 0 removes the gift; 1 repairs a gift that merged to quantity 2.
  function setGiftQuantity(giftLine, quantity, sections, sectionsUrl) {
    return postJson('cart/change.js', {
      id: giftLine.key,
      quantity: quantity,
      sections: sections || sectionsToRender(),
      sections_url: sectionsUrl || window.location.pathname
    });
  }

  // Section Rendering API: the drawer HTML without writing to the cart.
  function renderSections(sections, sectionsUrl) {
    var url = (sectionsUrl || window.location.pathname) + '?sections=' + encodeURIComponent(sections.join(','));
    return nativeFetch(url, { headers: { Accept: 'application/json' } }).then(function (r) {
      if (!r.ok) throw new Error('sections');
      return r.json();
    });
  }

  // ── Safety net ────────────────────────────────────────────────────────────

  function reconcile() {
    var trustDom = trustDomOnly;
    trustDomOnly = true;
    if (busy) {
      rerunAfterBusy = true;
      return;
    }
    var config = readConfig();
    if (!config) {
      pendingCart = null;
      return;
    }
    // The drawer was rendered by the server with this very page, so its config
    // is the cart's real state: no need to fetch cart.js to confirm a settled
    // gift. Only fetch when something actually needs doing.
    if (trustDom && !pendingCart && isSettled(config)) return;

    busy = true;
    // Reuse the cart payload carried by the cartUpdate event when it's complete,
    // so a quantity/variant change does not fire an extra GET cart.js purely to
    // re-check the gift threshold. Fall back to a fetch otherwise.
    var seeded = pendingCart;
    pendingCart = null;
    var cartReady =
      seeded && Array.isArray(seeded.items) && typeof seeded.total_price === 'number'
        ? Promise.resolve(seeded)
        : getCart();
    cartReady
      .then(function (cart) {
        var giftLine = findGiftLine(cart);
        var qualifies = giftIsOffered(config) && qualifyingSubtotal(cart) >= config.thresholdCents;

        // Remove: gift present but no longer earned (or GWP turned off / out of stock).
        if (giftLine && !qualifies) {
          return setGiftQuantity(giftLine, 0).then(rerender);
        }
        // Repair: two adds raced and the gift merged into quantity 2.
        if (giftLine && giftLine.quantity > 1) {
          return setGiftQuantity(giftLine, 1).then(rerender);
        }
        // Add: earned, in stock, and not already in the cart.
        if (qualifies && !giftLine) {
          return addGift(config.variantId).then(function (response) {
            if (response && response.status) return; // add failed (e.g. sold out); leave cart as-is
            return rerender(response);
          });
        }
        return null;
      })
      .catch(function () { /* swallow; try again on next cart update */ })
      .then(release);
  }

  function release() {
    busy = false;
    if (rerunAfterBusy) {
      rerunAfterBusy = false;
      // Any payload captured during the in-flight work predates the step that
      // just ran; seeding it could redo the gift step. Fetch the settled cart.
      pendingCart = null;
      scheduleReconcile();
    }
  }

  function scheduleReconcile(cartData, opts) {
    if (!(opts && opts.trustDom)) trustDomOnly = false;
    if (cartData && cartData[SYNCED_FLAG]) {
      pendingCart = null; // pre-gift snapshot; fetch the settled cart instead
    } else if (cartData && Array.isArray(cartData.items) && typeof cartData.total_price === 'number') {
      pendingCart = cartData;
    }
    if (debounceTimer) clearTimeout(debounceTimer);
    debounceTimer = setTimeout(reconcile, 120);
  }

  // ── Request helpers ───────────────────────────────────────────────────────

  function requestUrl(input) {
    if (typeof input === 'string') return input;
    if (input && typeof input.url === 'string') return input.url;
    return String(input || '');
  }

  // 'add' | 'change' | 'update' for a cart write, else null.
  function cartWriteKind(input, init) {
    var method = String((init && init.method) || (input && input.method) || 'GET').toUpperCase();
    if (method !== 'POST') return null;
    var path = requestUrl(input).split('?')[0].split('#')[0];
    var m = /\/cart\/(add|change|update)(\.js)?$/.exec(path);
    return m ? m[1] : null;
  }

  function parseBody(body) {
    if (typeof FormData !== 'undefined' && body instanceof FormData) return { type: 'form', form: body };
    if (typeof body === 'string' && body.charAt(0) === '{') {
      try { return { type: 'json', json: JSON.parse(body) }; } catch (e) { /* fall through */ }
    }
    return null;
  }

  function bodySections(parsed) {
    var s = parsed.type === 'form' ? parsed.form.get('sections') : parsed.json.sections;
    if (!s) return [];
    if (Array.isArray(s)) return s.map(String);
    return String(s).split(',').map(function (x) { return x.trim(); }).filter(Boolean);
  }

  function bodySectionsUrl(parsed) {
    return (parsed.type === 'form' ? parsed.form.get('sections_url') : parsed.json.sections_url) || null;
  }

  function copy(obj) {
    var out = {};
    for (var k in obj) {
      if (Object.prototype.hasOwnProperty.call(obj, k)) out[k] = obj[k];
    }
    return out;
  }

  function withBody(init, body) {
    var next = copy(init || {});
    next.body = body;
    return next;
  }

  function jsonResponse(data, original) {
    return new Response(JSON.stringify(data), {
      status: original.status,
      statusText: original.statusText,
      headers: { 'Content-Type': 'application/json' }
    });
  }

  // Fresh GWP state as the server rendered it after this write.
  function configFromSections(sections) {
    if (!sections || typeof DOMParser === 'undefined') return null;
    var parser = new DOMParser();
    var keys = Object.keys(sections);
    for (var i = 0; i < keys.length; i++) {
      var html = sections[keys[i]];
      if (typeof html !== 'string' || html.indexOf('data-dlux-gwp') === -1) continue;
      var el = parser.parseFromString(html, 'text/html').querySelector('[data-dlux-gwp]');
      if (el) return readConfigFrom(el);
    }
    return null;
  }

  // ── Prediction ────────────────────────────────────────────────────────────

  function variantPriceCents(id) {
    var key = String(id);
    if (Number.isFinite(priceHints[key])) return priceHints[key];
    var meta = window.ShopifyAnalytics && window.ShopifyAnalytics.meta && window.ShopifyAnalytics.meta.product;
    var variants = (meta && meta.variants) || [];
    for (var i = 0; i < variants.length; i++) {
      if (String(variants[i].id) === key && Number.isFinite(Number(variants[i].price))) return Number(variants[i].price);
    }
    return null;
  }

  // [{ id, quantity }] this add would put in the cart, or null if unreadable.
  function addedLines(parsed) {
    if (parsed.type === 'form') {
      var id = parsed.form.get('id');
      if (!id) return null; // items[] form posts: not predicted
      return [{ id: id, quantity: parseInt(parsed.form.get('quantity'), 10) || 1 }];
    }
    var json = parsed.json;
    if (Array.isArray(json.items)) {
      return json.items.map(function (it) { return { id: it.id, quantity: parseInt(it.quantity, 10) || 1 }; });
    }
    if (json.id) return [{ id: json.id, quantity: parseInt(json.quantity, 10) || 1 }];
    return null;
  }

  // Predicted qualifying subtotal after this write, or null when it can't be
  // read. List prices, so order-level discounts make it approximate; that only
  // affects which path is taken, never the outcome.
  function predictQualifying(kind, parsed, config) {
    if (kind === 'add') {
      var lines = addedLines(parsed);
      if (!lines || !lines.length) return null;
      var sum = config.qualifyingCents;
      for (var i = 0; i < lines.length; i++) {
        var price = variantPriceCents(lines[i].id);
        if (price == null) return null;
        sum += price * lines[i].quantity;
      }
      return sum;
    }
    if (kind === 'change' && parsed.type === 'json') {
      var line = parseInt(parsed.json.line, 10);
      var quantity = parseInt(parsed.json.quantity, 10);
      if (!Number.isFinite(line) || !Number.isFinite(quantity)) return null; // key-based change
      var input = document.querySelector('[data-index="' + line + '"][data-dlux-line-cents]');
      if (!input) return null;
      var lineCents = parseInt(input.getAttribute('data-dlux-line-cents'), 10);
      var lineQty = parseInt(input.getAttribute('data-dlux-line-qty'), 10);
      if (!Number.isFinite(lineCents) || !(lineQty > 0)) return null;
      return config.qualifyingCents + (lineCents / lineQty) * (quantity - lineQty);
    }
    return null;
  }

  // ── Path 1: add with the gift in the same request ─────────────────────────

  function bodyWithGift(parsed, variantId) {
    if (parsed.type === 'form') {
      var out = new FormData();
      parsed.form.forEach(function (value, key) {
        if (key === 'id') out.append('items[0][id]', value);
        else if (key === 'quantity') out.append('items[0][quantity]', value);
        else if (key === 'selling_plan') out.append('items[0][selling_plan]', value);
        else if (key.indexOf('properties[') === 0) out.append('items[0][properties]' + key.slice('properties'.length), value);
        else out.append(key, value);
      });
      if (!parsed.form.has('quantity')) out.append('items[0][quantity]', '1');
      out.append('items[1][id]', String(variantId));
      out.append('items[1][quantity]', '1');
      out.append('items[1][properties][' + GIFT_PROPERTY + ']', 'true');
      return out;
    }
    var json = copy(parsed.json);
    if (Array.isArray(json.items)) {
      json.items = json.items.concat([giftItem(variantId)]);
    } else {
      var first = { id: json.id, quantity: json.quantity || 1 };
      if (json.properties) first.properties = json.properties;
      if (json.selling_plan) first.selling_plan = json.selling_plan;
      delete json.id;
      delete json.quantity;
      delete json.properties;
      delete json.selling_plan;
      json.items = [first, giftItem(variantId)];
    }
    return JSON.stringify(json);
  }

  function addWithGift(input, init, parsed, config) {
    var sections = bodySections(parsed);
    var sectionsUrl = bodySectionsUrl(parsed);
    // A plain single-item add answers with the line itself; keep that shape
    // (product-form reads it, cart-drawer uses its id).
    var single = parsed.type === 'form' || !Array.isArray(parsed.json.items);

    // Resend the original request ONLY when the server clearly rejected the
    // combined one (nothing was added). After a network failure the add may
    // have gone through, and resending would double the product's quantity.
    var rejected = false;

    busy = true;
    return nativeFetch(input, withBody(init, bodyWithGift(parsed, config.variantId)))
      .then(function (response) {
        return response.json().then(function (data) { return { response: response, data: data }; });
      })
      .then(function (res) {
        var data = res.data;
        // Rejected (the product or the gift sold out, a quantity limit): send
        // the customer's original request so they get its normal outcome.
        if (!res.response.ok || !data || data.status || !Array.isArray(data.items)) {
          rejected = true;
          return null;
        }
        if (!data.sections) {
          return renderSections(sections, sectionsUrl).then(function (map) {
            data.sections = map;
            return finish(res, data);
          });
        }
        return finish(res, data);
      })
      .catch(function () { return null; })
      .then(function (result) {
        release();
        if (result) return result;
        if (rejected) return writeThenSync('add', input, init);
        // Unknown outcome: report a normal add error instead of risking a
        // double add. reconcile() then syncs the gift with whatever landed.
        scheduleReconcile();
        return new Response(JSON.stringify({ status: 500, message: 'Cart error', description: (window.cartStrings && window.cartStrings.error) || 'There was an error while updating your cart. Please try again.' }), {
          status: 500,
          headers: { 'Content-Type': 'application/json' }
        });
      });

    // Check the combined add against its own sections, correct a wrong guess,
    // and hand back the caller's usual response shape.
    function finish(res, data) {
      var giftLine = findGiftLine(data);
      var post = configFromSections(data.sections);
      var fix = null;
      if (giftLine && post) {
        var earned = giftIsOffered(post) && post.qualifyingCents >= post.thresholdCents;
        // Price guess was optimistic (e.g. an order discount): take it back out.
        if (!earned) fix = setGiftQuantity(giftLine, 0, sections, sectionsUrl);
        // The cart already held a gift the page did not know about: merged to 2.
        else if (post.giftQty > 1) fix = setGiftQuantity(giftLine, 1, sections, sectionsUrl);
      }
      return (fix || Promise.resolve(null)).then(function (fixResponse) {
        var nonGift = data.items.filter(function (it) { return !isGiftLine(it); });
        var shaped = single ? copy(nonGift[0] || data.items[0]) : { items: nonGift };
        shaped.sections = fixResponse && !fixResponse.status && fixResponse.sections ? fixResponse.sections : data.sections;
        shaped[CHECKED_FLAG] = true;
        return jsonResponse(shaped, res.response);
      });
    }
  }

  // ── Path 2: quantity change without sections, then the gift step ──────────

  function changeThenSync(input, init, parsed, config) {
    var sections = bodySections(parsed);
    var sectionsUrl = bodySectionsUrl(parsed);
    var stripped = copy(parsed.json);
    delete stripped.sections;
    delete stripped.sections_url;

    busy = true;
    return nativeFetch(input, withBody(init, JSON.stringify(stripped)))
      .then(function (response) {
        if (!response.ok) return { response: response };
        return response.clone().json().then(function (data) { return { response: response, data: data }; });
      })
      .then(function (res) {
        var data = res.data;
        // Errors go straight back; cart.js handles them without sections.
        if (!data || data.status || data.errors) return res.response;

        // Exact from here on: /cart/change returns the whole cart.
        var earned = qualifyingSubtotal(data) >= config.thresholdCents;
        var giftLine = findGiftLine(data);
        var step = null;
        var addedGift = false;
        if (earned && !giftLine) {
          step = addGift(config.variantId, sections, sectionsUrl);
          addedGift = true;
        } else if (!earned && giftLine) {
          step = setGiftQuantity(giftLine, 0, sections, sectionsUrl);
        } else if (giftLine && giftLine.quantity > 1) {
          step = setGiftQuantity(giftLine, 1, sections, sectionsUrl);
        }

        var sectionsReady = step
          ? step.then(function (stepResponse) {
              if (stepResponse && !stepResponse.status && stepResponse.sections) return stepResponse;
              addedGift = false; // gift step failed; render the cart as it stands
              return renderSections(sections, sectionsUrl).then(function (map) { return { sections: map }; });
            })
          // Prediction was off and nothing needs doing: just fetch the render.
          : renderSections(sections, sectionsUrl).then(function (map) { return { sections: map }; });

        return sectionsReady.then(function (final) {
          var merged = copy(data);
          merged.sections = final.sections;
          // item_count drives the drawer's empty state (cart.js toggles
          // .is-empty from it), so it must describe the cart AFTER the gift
          // step: removing the last product also removes the gift.
          if (typeof final.item_count === 'number') merged.item_count = final.item_count;
          else if (addedGift && typeof data.item_count === 'number') merged.item_count = data.item_count + 1;
          merged[SYNCED_FLAG] = true;
          merged[CHECKED_FLAG] = true;
          return jsonResponse(merged, res.response);
        });
      })
      .then(
        function (result) { release(); return result; },
        function () {
          // Network trouble after the change went through: fall back to a
          // plain render so the caller still gets sections.
          release();
          return writeThenSync('change', input, init);
        }
      );
  }

  // ── Path 3: write as-is, then the gift step if its sections say so ───────

  function writeThenSync(kind, input, init) {
    var parsed = parseBody(init && init.body);
    var sectionsUrl = parsed ? bodySectionsUrl(parsed) : null;
    return nativeFetch(input, init).then(function (response) {
      if (!response.ok) return response;
      return response.clone().json().then(function (data) {
        // Error payloads and writes that did not ask for sections pass through
        // untouched; reconcile() still handles the gift for those.
        if (!data || data.status || data.errors || !data.sections) return response;
        var config = configFromSections(data.sections);
        if (!config) return response;

        var earned = giftIsOffered(config) && config.qualifyingCents >= config.thresholdCents;
        // /cart/change and /cart/update return the whole cart, so the gift
        // line's key is right here. /cart/add returns only the added lines.
        var giftLine = kind !== 'add' ? findGiftLine(data) : null;
        var step = null;
        var addedGift = false;
        if (earned && !config.hasGift) {
          step = function (s) { return addGift(config.variantId, s, sectionsUrl); };
          addedGift = true;
        } else if (!earned && config.hasGift && giftLine) {
          step = function (s) { return setGiftQuantity(giftLine, 0, s, sectionsUrl); };
        } else if (config.giftQty > 1 && giftLine) {
          step = function (s) { return setGiftQuantity(giftLine, 1, s, sectionsUrl); };
        }

        if (!step) {
          // Settled. Flag it so the cartUpdate that follows skips reconcile().
          // A doubled gift behind an add (no key here) is left to reconcile().
          if (config.giftQty > 1) return response;
          var checked = copy(data);
          checked[CHECKED_FLAG] = true;
          return jsonResponse(checked, response);
        }
        // reconcile() is mid add/remove: leave it to finish, or the gift could
        // be added twice and merge into one line with quantity 2.
        if (busy) return response;

        busy = true;
        return step(Object.keys(data.sections))
          .then(function (giftResponse) {
            // Gift step failed (e.g. gift just sold out): the customer's own
            // change stands and reconcile() retries on the next update.
            if (!giftResponse || giftResponse.status || !giftResponse.sections) return response;
            // Keep the caller's response exactly as it was (product-form reads
            // the added line; cart.js checks items[line - 1] against the line
            // number it just changed), but hand it the post-gift sections.
            var merged = copy(data);
            merged.sections = giftResponse.sections;
            if (typeof data.item_count === 'number') {
              merged.item_count = typeof giftResponse.item_count === 'number'
                ? giftResponse.item_count
                : data.item_count + (addedGift ? 1 : 0);
            }
            merged[SYNCED_FLAG] = true;
            merged[CHECKED_FLAG] = true;
            return jsonResponse(merged, response);
          })
          .catch(function () { return response; })
          .then(function (result) {
            release();
            return result;
          });
      }, function () {
        return response; // not JSON (e.g. a non-AJAX form post)
      });
    });
  }

  function interceptCartWrite(kind, input, init) {
    var config = readConfig();
    var parsed = parseBody(init && init.body);
    if (!busy && giftIsOffered(config) && parsed && bodySections(parsed).length) {
      var predicted = predictQualifying(kind, parsed, config);
      if (predicted != null) {
        var willEarn = predicted >= config.thresholdCents;
        if (kind === 'add' && willEarn && !config.hasGift) {
          return addWithGift(input, init, parsed, config);
        }
        if (kind === 'change' && willEarn !== config.hasGift) {
          return changeThenSync(input, init, parsed, config);
        }
      }
    }
    return writeThenSync(kind, input, init);
  }

  window.fetch = function (input, init) {
    var kind = cartWriteKind(input, init);
    if (!kind) return nativeFetch(input, init);
    return interceptCartWrite(kind, input, init);
  };

  // ── Triggers ──────────────────────────────────────────────────────────────

  function onLoad() {
    scheduleReconcile(null, { trustDom: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', onLoad);
  } else {
    onLoad();
  }

  document.addEventListener('shopify:section:load', onLoad);

  // Restored from the back/forward cache: the DOM may be older than the cart.
  window.addEventListener('pageshow', function (event) {
    if (event.persisted) scheduleReconcile();
  });

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, function (event) {
      // Our own cartUpdate: the step that published it left the cart settled.
      if (event && event.source === 'dlux-cart-gift') return;
      // A write this script already checked against its own sections.
      if (event && event.cartData && event.cartData[CHECKED_FLAG]) return;
      scheduleReconcile(event && event.cartData);
    });
  }
})();
